"use client";

import React, { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useURLFilter } from "./useCategoryFilter";
import { Range, getTrackBackground } from "react-range";

const MIN = 100;
const MAX = 5000;
const STEP = 100;

interface Props {
  tripTypes: any[];
  duration: any[];
  onApply?: () => void;
}

const PriceRangeSection = ({ tripTypes, duration, onApply }: Props) => {
  const { setFilter, getFilter } = useURLFilter();
  const router = useRouter();
  const pathname = usePathname();

  const [values, setValues] = useState<number[]>([
    Number(getFilter("min_price") || MIN),
    Number(getFilter("max_price") || MAX),
  ]);

  const tripType = getFilter("trip_type", "");

  const handleSliderChange = (vals: number[]) => {
    const [prevMin, prevMax] = values;
    const [newMin, newMax] = vals;

    setValues(vals);

    if (newMin !== prevMin) setFilter("min_price", newMin);
    if (newMax !== prevMax) setFilter("max_price", newMax);
  };
  const selectedMinRating = Number(getFilter("min_rating") || 0);

  return (
    <div className="flex flex-col gap-4">
      {/* PRICE RANGE */}
      <h3 className="typography-large-body">Price Range</h3>

      <div className="w-full px-2 md:px-4">
        <Range
          step={STEP}
          min={MIN}
          max={MAX}
          values={values}
          onChange={handleSliderChange}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className="h-2 w-full rounded-lg"
              style={{
                background: getTrackBackground({
                  values,
                  colors: ["#a78bfa", "#7c3aed", "#e5e7eb"],
                  min: MIN,
                  max: MAX,
                }),
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div
              {...props}
              className="h-5 w-5 rounded-full bg-violet-500 shadow-md"
            />
          )}
        />
        <div className="flex justify-between text-sm mt-2">
          <span>Rs. {values[0]}</span>
          <span>Rs. {values[1]}</span>
        </div>
      </div>

      <hr />

      {/* USER RATING */}
      <div>
        <p className="typography-large-body mb-2">User Rating</p>
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() =>
                setFilter("min_rating", selectedMinRating === n ? null : n)
              }
              className={`text-2xl ${
                selectedMinRating >= n ? "text-yellow-400" : "text-gray-300"
              }`}
            >
              ★
            </button>
          ))}
        </div>
      </div>

      <hr />

      {/* TRIP TYPE */}
      <div>
        <p>Trip Type</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {tripTypes.map((item) => (
            <button
              key={item.id}
              onClick={() =>
                setFilter(
                  "trip_type",
                  tripType === item.slug ? null : item.slug
                )
              }
              className={`px-4 py-2 rounded-full border text-sm transition ${
                tripType === item.slug
                  ? "bg-yellow-500 text-white border-yellow-500"
                  : "border-gray-300"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      <hr />

      {/* DURATION */}
      <div>
        <p>Duration</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {duration.map((d) => {
            const active = getFilter("duration") === d;
            return (
              <button
                key={d}
                onClick={() => setFilter("duration", active ? null : d)}
                className={`px-4 py-2 rounded-full border text-sm ${
                  active
                    ? "bg-yellow-500 text-white border-yellow-500"
                    : "border-gray-300"
                }`}
              >
                {d} Days
              </button>
            );
          })}
        </div>
      </div>

      <hr />

      {/* ACTIONS */}
      <div className="flex gap-2">
        <button
          onClick={() => router.push(pathname, { scroll: false })}
          className="flex-1 border border-yellow-500 text-sm py-2"
        >
          Reset
        </button>

        <button
          onClick={onApply}
          className="flex-1 bg-yellow-500 text-white text-sm py-2"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default PriceRangeSection;
